// Base58 (Bitcoin/Solana alphabet) encode/decode — dependency-free.
//
// Phantom's `signMessage` returns the signature as raw bytes, and the backend's
// ed25519 verifier (tweetnacl + bs58) expects base58 strings for both the wallet
// address and the signature. We keep our own tiny implementation rather than
// pulling in `bs58` to mirror the project's minimal-deps style.

const ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
const BASE = ALPHABET.length;

const INDEX: Record<string, number> = {};
for (let i = 0; i < ALPHABET.length; i += 1) INDEX[ALPHABET[i]!] = i;

/** Encode bytes to a base58 string (big-endian, with leading-zero preservation). */
export function base58Encode(bytes: Uint8Array): string {
  if (bytes.length === 0) return '';
  let zeros = 0;
  while (zeros < bytes.length && bytes[zeros] === 0) zeros += 1;

  const digits: number[] = [];
  for (let i = zeros; i < bytes.length; i += 1) {
    let carry = bytes[i]!;
    for (let j = 0; j < digits.length; j += 1) {
      carry += digits[j]! << 8;
      digits[j] = carry % BASE;
      carry = (carry / BASE) | 0;
    }
    while (carry > 0) {
      digits.push(carry % BASE);
      carry = (carry / BASE) | 0;
    }
  }

  let out = '1'.repeat(zeros);
  for (let i = digits.length - 1; i >= 0; i -= 1) out += ALPHABET[digits[i]!];
  return out;
}

/** Decode a base58 string to bytes. Throws on an invalid character. */
export function base58Decode(str: string): Uint8Array {
  if (str.length === 0) return new Uint8Array();
  let zeros = 0;
  while (zeros < str.length && str[zeros] === '1') zeros += 1;

  const bytes: number[] = [];
  for (let i = zeros; i < str.length; i += 1) {
    const value = INDEX[str[i]!];
    if (value === undefined) throw new Error(`invalid base58 character: ${str[i]}`);
    let carry = value;
    for (let j = 0; j < bytes.length; j += 1) {
      carry += bytes[j]! * BASE;
      bytes[j] = carry & 0xff;
      carry >>= 8;
    }
    while (carry > 0) {
      bytes.push(carry & 0xff);
      carry >>= 8;
    }
  }

  const out = new Uint8Array(zeros + bytes.length);
  for (let i = 0; i < bytes.length; i += 1) {
    out[zeros + bytes.length - 1 - i] = bytes[i]!;
  }
  return out;
}
